/**
 * Buildium Prompt Templates
 * Templates for extracting fields from conversations for Buildium push integrations
 */
import { UNKNOWN_CONTACT_FIRST_NAME, UNKNOWN_CONTACT_LAST_NAME, } from '@super/shared-types';
import { z } from 'zod';
// ============================================================================
// Work Order
// ============================================================================
/**
 * Schema for Buildium work order field extraction response
 *
 * LLM extracts these fields from the conversation:
 * - Task.Title: Concise title for the work order task
 * - Task.Priority: Low, Normal, or High based on urgency
 * - EntryAllowed: Whether vendor can enter (Unknown, Yes, No)
 * - EntryNotes: Entry instructions (only if Yes or No)
 * - VendorNotes: Special instructions for the vendor
 *
 * Fields NOT extracted by LLM (set by push service):
 * - VendorId: From integration.metadata.defaultVendorId
 * - Task.AssignedToUserId: From integration.metadata.defaultAssignedToUserId
 * - Task.Status: Always 'New' for new work orders
 * - WorkDetails: From jobData.summary
 */
export const BuildiumWorkOrderSchema = z.object({
    title: z
        .string()
        .max(127)
        .describe('Concise work order task title (max 127 chars)'),
    priority: z.enum(['Low', 'Normal', 'High']).describe('Task priority level'),
    entryAllowed: z
        .enum(['Unknown', 'Yes', 'No'])
        .describe('Whether vendor entry is allowed'),
    entryNotes: z
        .string()
        .nullable()
        .describe('Entry instructions (only if entryAllowed is Yes or No, null otherwise)'),
    vendorNotes: z
        .string()
        .nullable()
        .describe('Special instructions for the vendor (null if none)'),
    reasoning: z.string().describe('Brief explanation of the field selections'),
});
/**
 * Prompt template for extracting Buildium work order fields
 * Used to generate title, priority, entry access info, and vendor notes
 */
export const buildiumWorkOrderTemplate = {
    name: 'buildium-work-order',
    version: '1.0.0',
    systemPrompt: `You are a property management assistant that extracts work order information from maintenance conversations.

Your task is to carefully analyze the FULL conversation transcript between the resident and the AI assistant to extract key work order fields. Pay close attention to everything the resident says.

TITLE GUIDELINES:
- Create a brief, actionable title that describes the main issue (maximum 127 characters)
- Include the specific issue type (e.g., "leaking", "broken", "not working")
- Include the affected item/area (e.g., "kitchen faucet", "bathroom toilet", "bedroom window")
- Include unit/location if mentioned in the conversation
- Good examples: "Leaking kitchen faucet - Unit 204", "Toilet won't flush in master bathroom", "AC not cooling - apartment 5B"
- Bad examples: "Maintenance request", "Issue reported", "Problem" (too vague)

PRIORITY GUIDELINES:
- "High": ONLY for true emergencies that pose immediate risk:
  * Active water leaks causing damage
  * No heat when outside temperature is below freezing
  * Gas smell or suspected gas leak
  * Electrical hazards (sparking, burning smell)
  * Security issues (broken locks, broken windows)
  * Sewage backup
  * No hot water in winter
- "Normal": Most maintenance issues (DEFAULT - use this for typical repairs)
  * Appliances not working properly
  * Minor plumbing issues (slow drains, dripping faucets)
  * HVAC issues that aren't emergencies
  * Cosmetic damage
- "Low": Non-urgent, quality-of-life improvements
  * Cosmetic requests
  * Minor inconveniences
  * Scheduled maintenance

ENTRY ACCESS - Read the conversation carefully for these indicators:
- "Unknown": DEFAULT - Use when entry permission was NOT explicitly discussed
- "Yes": ONLY if the resident explicitly said something like:
  * "You can come in anytime"
  * "I'll leave a key"
  * "Someone will be there to let you in"
  * "Feel free to enter"
  * "I don't need to be there"
- "No": ONLY if the resident explicitly said something like:
  * "I need to be there"
  * "Don't enter without me"
  * "I want to be present"
  * "Call me before coming"

ENTRY NOTES - Only populate if entryAllowed is "Yes" or "No":
- Extract ONLY entry-related details:
  * Access codes or gate codes
  * Key location (under mat, with neighbor, lockbox code)
  * Pet information (type, name, behavior)
  * Preferred times or availability
  * Special entry instructions (use back door, ring bell, etc.)
- DO NOT include: contact name, phone number, issue description, or general problem details
- If entryAllowed is "Unknown", this MUST be null
- Example: "Lockbox code 1234, small dog in unit, available after 3pm"

VENDOR NOTES - ONLY include information that is NOT already in the issue summary:
- DO NOT repeat: the main issue description, contact information, phone numbers, or resolution status
- DO include (if mentioned and not in summary):
  * How long the issue has been happening
  * What the resident has already tried to fix it
  * Specific location within the unit (e.g., "master bathroom", "kitchen near sink")
  * Any relevant history (e.g., "this happened before last month")
  * Parts that might be needed based on the description
- Set to null if there's nothing useful to add beyond what's in the summary`,
    userPrompt: `Analyze this maintenance conversation and extract work order fields:

ISSUE SUMMARY:
{{summary}}

FULL CONVERSATION TRANSCRIPT:
{{transcript}}

Instructions:
1. Read the ENTIRE transcript carefully - important details are often mentioned later in the conversation
2. Extract a specific, descriptive title (not generic like "Maintenance request")
3. Only set priority to "High" for true emergencies
4. Only set entryAllowed to "Yes" or "No" if the resident EXPLICITLY discussed entry permission
5. entryNotes MUST be null if entryAllowed is "Unknown"
6. entryNotes should ONLY contain entry-related info (access codes, pets, availability) - NOT contact info or issue details
7. vendorNotes should ONLY contain additional context NOT already in the summary - set to null if nothing new to add`,
    variables: ['summary', 'transcript'],
    metadata: {
        maxTokens: 1000,
        responseFormat: 'json_object',
    },
};
// ============================================================================
// Resident Request
// ============================================================================
/**
 * Schema for Buildium resident request field extraction response
 *
 * LLM extracts these fields from the conversation:
 * - Priority: Low, Normal, or High based on urgency
 * - CategoryId: Selected from available categories
 * - IsEntryPermittedByResident: Whether entry is allowed (true, false, null)
 * - DoesResidentHavePets: Whether resident has pets (true, false, null)
 * - ResidentEntryNotes: Entry instructions
 *
 * Fields NOT extracted by LLM (set by push service):
 * - Title: From eventData.title
 * - UnitAgreementId: From integration.metadata.unitAgreementId
 * - RequestedByEntityId: From integration.metadata.requestedByEntityId
 * - AssignedToUserId: From integration.metadata.defaultAssignedToUserId
 * - TaskStatus: Always 'New' for new requests
 * - Description: From jobData.summary
 */
export const BuildiumResidentRequestSchema = z.object({
    priority: z
        .enum(['Low', 'Normal', 'High'])
        .describe('Request priority level'),
    categoryId: z
        .number()
        .nullable()
        .describe('Selected category ID from available categories (null if none match)'),
    isEntryPermittedByResident: z
        .boolean()
        .nullable()
        .describe('Whether resident permits entry (true=yes, false=no, null=not discussed)'),
    doesResidentHavePets: z
        .boolean()
        .nullable()
        .describe('Whether resident has pets (true=yes, false=no, null=not mentioned)'),
    residentEntryNotes: z
        .string()
        .nullable()
        .describe('Entry instructions from resident (null if none provided)'),
    reasoning: z.string().describe('Brief explanation of the field selections'),
});
/**
 * Prompt template for extracting Buildium resident request fields
 * Used to extract priority, category selection, entry access info, and pet info
 *
 * Note: Title comes from eventData.title, not LLM extraction
 */
export const buildiumResidentRequestTemplate = {
    name: 'buildium-resident-request',
    version: '1.0.0',
    systemPrompt: `You are a property management assistant that extracts resident request information from maintenance conversations.

Your task is to carefully analyze the FULL conversation transcript between the resident and the AI assistant to extract key request fields. Pay close attention to everything the resident says.

PRIORITY GUIDELINES:
- "High": ONLY for true emergencies that pose immediate risk:
  * Active water leaks causing damage
  * No heat when outside temperature is below freezing
  * Gas smell or suspected gas leak
  * Electrical hazards (sparking, burning smell)
  * Security issues (broken locks, broken windows)
  * Sewage backup
  * No hot water in winter
- "Normal": Most maintenance issues (DEFAULT - use this for typical repairs)
  * Appliances not working properly
  * Minor plumbing issues (slow drains, dripping faucets)
  * HVAC issues that aren't emergencies
  * Cosmetic damage
- "Low": Non-urgent, quality-of-life improvements
  * Cosmetic requests
  * Minor inconveniences
  * Scheduled maintenance

CATEGORY SELECTION:
- You will be provided with a list of available categories
- Select the MOST APPROPRIATE category that matches the type of issue
- If no category seems to fit well, set categoryId to null

ENTRY PERMISSION - Read the conversation carefully for these indicators:
- null (DEFAULT): Use when entry permission was NOT explicitly discussed
- true: ONLY if the resident explicitly said something like:
  * "You can come in anytime"
  * "I'll leave a key"
  * "Someone will be there to let you in"
  * "Feel free to enter"
  * "I don't need to be there"
- false: ONLY if the resident explicitly said something like:
  * "I need to be there"
  * "Don't enter without me"
  * "I want to be present"
  * "Call me before coming"

PET INFORMATION:
- null (DEFAULT): Use when pets were NOT mentioned in the conversation
- true: If the resident mentioned having pets (dog, cat, etc.)
- false: If the resident explicitly said they don't have pets

ENTRY NOTES - Only populate if entry was discussed:
- Extract ONLY entry-related details:
  * Access codes or gate codes
  * Key location (under mat, with neighbor, lockbox code)
  * Pet information (type, name, behavior)
  * Preferred times or availability
  * Special entry instructions (use back door, ring bell, etc.)
- DO NOT include: contact name, phone number, issue description, or general problem details
- Set to null if entry was not discussed`,
    userPrompt: `Analyze this maintenance conversation and extract resident request fields:

ISSUE SUMMARY:
{{summary}}

FULL CONVERSATION TRANSCRIPT:
{{transcript}}

AVAILABLE CATEGORIES:
{{categories}}

Instructions:
1. Read the ENTIRE transcript carefully - important details are often mentioned later in the conversation
2. Only set priority to "High" for true emergencies
3. Select the most appropriate category from the available list (null if none fit)
4. Select a subcategory if available and appropriate (null if none fit or no subcategories)
5. Only set isEntryPermittedByResident to true/false if EXPLICITLY discussed (null otherwise)
6. Only set doesResidentHavePets to true/false if pets were MENTIONED (null otherwise)
7. residentEntryNotes should ONLY contain entry-related info - NOT contact info or issue details`,
    variables: ['summary', 'transcript', 'categories'],
    metadata: {
        maxTokens: 1000,
        responseFormat: 'json_object',
    },
};
// ============================================================================
// Rental Owner Request
// ============================================================================
/**
 * Schema for Buildium rental owner request field extraction response
 *
 * LLM extracts these fields from the conversation:
 * - Priority: Low, Normal, or High based on urgency
 * - CategoryId: Selected from available categories
 *
 * Fields NOT extracted by LLM (set by push service):
 * - Title: From jobData.title
 * - RequestedByRentalOwnerId: From integration.metadata.requestedByRentalOwnerId
 * - AssignedToUserId: From integration.metadata.defaultAssignedToUserId
 * - TaskStatus: Always 'New' for new requests
 * - Description: From jobData.summary
 */
export const BuildiumRentalOwnerRequestSchema = z.object({
    priority: z
        .enum(['Low', 'Normal', 'High'])
        .describe('Request priority level'),
    categoryId: z
        .number()
        .nullable()
        .describe('Selected category ID from available categories (null if none match)'),
    reasoning: z.string().describe('Brief explanation of the field selections'),
});
/**
 * Prompt template for extracting Buildium rental owner request fields
 * Used to extract priority and category selection from conversations
 *
 * Note: This is a simplified version compared to resident requests -
 * rental owner requests don't have entry-related fields
 */
export const buildiumRentalOwnerRequestTemplate = {
    name: 'buildium-rental-owner-request',
    version: '1.0.0',
    systemPrompt: `You are a property management assistant that extracts rental owner request information from conversations.

Your task is to analyze the conversation and extract key request fields for creating a rental owner request in Buildium.

PRIORITY GUIDELINES:
- "High": ONLY for true emergencies or urgent issues that pose immediate risk:
  * Property damage requiring immediate attention
  * Safety or security concerns
  * Issues affecting multiple units or tenants
  * Legal compliance deadlines
- "Normal": Most requests (DEFAULT - use this for typical owner requests)
  * Property maintenance questions
  * Financial inquiries
  * Tenant-related concerns
  * Reporting issues
- "Low": Non-urgent, informational requests
  * General questions
  * Future planning
  * Minor updates

CATEGORY SELECTION:
- You will be provided with a list of available categories
- Select the MOST APPROPRIATE category that matches the type of issue or request
- If no category seems to fit well, set categoryId to null`,
    userPrompt: `Analyze this conversation and extract rental owner request fields:

ISSUE SUMMARY:
{{summary}}

FULL CONVERSATION TRANSCRIPT:
{{transcript}}

AVAILABLE CATEGORIES:
{{categories}}

Instructions:
1. Read the conversation carefully to understand the nature of the request
2. Only set priority to "High" for true emergencies or urgent matters
3. Select the most appropriate category from the available list (null if none fit)`,
    variables: ['summary', 'transcript', 'categories'],
    metadata: {
        maxTokens: 500,
        responseFormat: 'json_object',
    },
};
// ============================================================================
// Contact Request
// ============================================================================
/**
 * Schema for Buildium contact request field extraction response
 *
 * LLM extracts these fields from the conversation:
 * - Priority: Low, Normal, or High (preferred: Normal)
 * - CategoryId: Selected from available categories
 * - Contact details: FirstName, LastName, Email, Phone parsed from summary
 *
 * Fields NOT extracted by LLM (set by push service):
 * - Title: From jobData.title
 * - AssignedToUserId: From integration.metadata.defaultAssignedToUserId
 * - TaskStatus: Always 'New' for new requests
 * - Description: From jobData.summary
 */
export const BuildiumContactRequestSchema = z.object({
    priority: z
        .enum(['Low', 'Normal', 'High'])
        .describe('Request priority level (prefer Normal unless urgency is clear)'),
    categoryId: z
        .number()
        .nullable()
        .describe('A category ID from the provided list. MUST be null if no category fits. Do NOT invent category IDs.'),
    firstName: z
        .string()
        .nullable()
        .describe(`Contact first name ONLY — return the raw name string or null. Do NOT include explanations, reasoning, or sentences. Extract only real person names explicitly stated in the text. If the name is a placeholder like "${UNKNOWN_CONTACT_FIRST_NAME} ${UNKNOWN_CONTACT_LAST_NAME}", return "${UNKNOWN_CONTACT_FIRST_NAME}".`),
    lastName: z
        .string()
        .nullable()
        .describe(`Contact last name ONLY — return the raw name string or null. Do NOT include explanations, reasoning, or sentences. Extract only real person names explicitly stated in the text. If the name is a placeholder like "${UNKNOWN_CONTACT_FIRST_NAME} ${UNKNOWN_CONTACT_LAST_NAME}", return null.`),
    email: z
        .string()
        .nullable()
        .describe('Contact email. MUST be null unless an explicit email address (e.g. user@domain.com) appears in the text. Never guess or fabricate.'),
    phone: z
        .string()
        .nullable()
        .describe('Contact phone number formatted as (XXX) XXX-XXXX. MUST be null unless a phone number is explicitly stated in the text. Never guess or fabricate.'),
    reasoning: z.string().describe('Brief explanation of the field selections'),
});
/**
 * Prompt template for extracting Buildium contact request fields
 * Used to extract priority, category selection, and contact details from conversations
 *
 * Contact requests are for general inquiries from people who are not residents or rental owners
 */
export const buildiumContactRequestTemplate = {
    name: 'buildium-contact-request',
    version: '1.0.0',
    systemPrompt: `You are a property management assistant that extracts contact request information from conversations.

Your task is to analyze the conversation and extract key request fields for creating a contact request in Buildium.

CRITICAL RULE: Only extract values that are EXPLICITLY stated in the provided text. If a field is not clearly present, you MUST return null. Never guess, infer, or fabricate any value.

PRIORITY GUIDELINES:
- "Normal": DEFAULT - use for most general inquiries and requests
- "High": Only for urgent matters requiring immediate attention
- "Low": For non-urgent, informational requests

When in doubt, use "Normal" priority.

CATEGORY SELECTION:
- You will be provided with a list of available categories with their IDs
- Select the MOST APPROPRIATE category that matches the type of inquiry or request
- You MUST only use a categoryId that appears in the provided list
- If no category seems to fit well, set categoryId to null

CONTACT DETAIL EXTRACTION:
Look for contact information in the summary. The summary may contain a section like:
"Contact Information - Name: John Doe, Phone: 555-1234, Email: john@example.com"

Extract:
- firstName: The contact's real first name (first part of the name before any space). If the name is "${UNKNOWN_CONTACT_FIRST_NAME} ${UNKNOWN_CONTACT_LAST_NAME}" or another placeholder, use "${UNKNOWN_CONTACT_FIRST_NAME}" as firstName.
- lastName: The contact's real last name (remaining part of the name after the first space). If the name is a placeholder like "${UNKNOWN_CONTACT_FIRST_NAME} ${UNKNOWN_CONTACT_LAST_NAME}", set lastName to null.
- email: ONLY extract if an explicit email address (e.g. user@domain.com) is clearly written in the text. If no email is present, you MUST return null. Do NOT guess or fabricate.
- phone: ONLY extract if a phone number is explicitly stated. Format as (XXX) XXX-XXXX. If no phone number is present, you MUST return null. Do NOT guess or fabricate.

CRITICAL: firstName and lastName must contain ONLY the name string itself (e.g. "John", "Doe") or null. Never put explanations, reasoning, or sentences in name fields.
If the name is a single word, use it as firstName and set lastName to null.`,
    userPrompt: `Analyze this conversation and extract contact request fields:

ISSUE SUMMARY:
{{summary}}

FULL CONVERSATION TRANSCRIPT:
{{transcript}}

AVAILABLE CATEGORIES:
{{categories}}

Instructions:
1. Read the conversation carefully to understand the nature of the request
2. Use "Normal" priority unless there's a clear reason for High or Low
3. Select the most appropriate category from the available list (null if none fit). Only use IDs from the list above.
4. Extract contact details ONLY if explicitly stated in the text. Return null for any field not clearly present.`,
    variables: ['summary', 'transcript', 'categories'],
    metadata: {
        maxTokens: 600,
        responseFormat: 'json_object',
    },
};
//# sourceMappingURL=buildium.js.map